import type { ChartDataConfig, MetricConfig } from '../models/chart-config.model';
import { CHART_TYPE_REQUIREMENTS } from '../services/chart-config-change.types';

function namedMetrics(metrics: MetricConfig[] | undefined): MetricConfig[] {
	if (!Array.isArray(metrics)) {
		return [];
	}
	return metrics.filter(
		m => typeof m?.name === 'string' && m.name.trim().length > 0
	);
}

/**
 * Shape AI `data` patches to {@link CHART_TYPE_REQUIREMENTS}:
 * - truncate when over `maxMetrics` (donut/number/scatter)
 * - drop `metrics` when under `minMetrics` so merge keeps prior metrics
 *   (scatter must not cement a 1-metric config from a failed tool salvage)
 */
export function constrainAiChartDataForRequirements(
	data: ChartDataConfig | Record<string, unknown> | undefined,
	chartType: string | undefined
): ChartDataConfig | Record<string, unknown> | undefined {
	if (!data || typeof data !== 'object' || Array.isArray(data)) {
		return data;
	}
	if (!chartType?.trim()) {
		return data;
	}
	const requirements = CHART_TYPE_REQUIREMENTS[chartType];
	if (!requirements || !('metrics' in data)) {
		return data;
	}

	const chartData = data as ChartDataConfig;
	const metrics = chartData.metrics;
	if (!Array.isArray(metrics)) {
		return data;
	}

	const named = namedMetrics(metrics);
	if (named.length < requirements.minMetrics) {
		const { metrics: _omit, ...rest } = data as Record<string, unknown>;
		return rest;
	}

	if (
		requirements.maxMetrics != null &&
		named.length > requirements.maxMetrics
	) {
		return {
			...data,
			metrics: named.slice(0, requirements.maxMetrics),
		};
	}

	return data;
}

/** Prompt suffix so card-editor agent respects chart-type metric rules. */
export function cardEditorChartRequirementsHint(
	chartType: string | undefined
): string | null {
	const normalized = chartType?.trim().toLowerCase();
	if (normalized === 'scatter') {
		return (
			'Card context: chartType is scatter. Configure exactly 2 named metrics ' +
			'(horizontal/X and vertical/Y) and an optional breakdown dimension. ' +
			'If the user only named one metric, ask which second metric to use for the other axis ' +
			'before calling update_card_config. Do not call update_card_config with fewer than 2 metrics.'
		);
	}
	return null;
}

/** Append chart-type requirements hint to the user prompt when needed. */
export function withCardEditorChartRequirementsHint(
	prompt: string,
	chartType: string | undefined
): string {
	const hint = cardEditorChartRequirementsHint(chartType);
	if (!hint) {
		return prompt;
	}
	const trimmed = prompt.trimEnd();
	return `${trimmed}\n\n[${hint}]`;
}
